import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { Story } from '../types';
import { X, ExternalLink, Plus, QrCode, CheckCircle, Smartphone, Upload, DollarSign, User, Loader2, Eye, Users, Volume2, VolumeX, Gift } from 'lucide-react';
import { addXpToProfile, XP_VALUES } from '../services/gamificationService';
import { PartnerCarousel } from './PartnerCarousel';

interface SoldState {
    soldAt: string; // ISO String
    originalExpiresAt: string;
    extendedExpiresAt: string;
}

interface StoriesProps {
    onViewProfile: (userId: string) => void;
    isPremiumOrVip?: boolean;
    onRequirePremium?: (featureName: string) => void;
}

export const Stories: React.FC<StoriesProps> = ({ onViewProfile, isPremiumOrVip = false, onRequirePremium }) => {
    const [stories, setStories] = useState<Story[]>([]);
    const [selectedStory, setSelectedStory] = useState<Story | null>(null);
    const [loading, setLoading] = useState(true);
    const [showAdModal, setShowAdModal] = useState(false);
    const [newAd, setNewAd] = useState<{ title: string, media_url: string, duration: number, link_url: string }>({ title: '', media_url: '', duration: 24, link_url: '' });
    const [showEmbedLink, setShowEmbedLink] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [step, setStep] = useState(1); // 1: Form, 2: Payment/QR
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [currentUserId, setCurrentUserId] = useState<string | null>(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [soldStories, setSoldStories] = useState<Record<string, SoldState>>({});
    const [storyViews, setStoryViews] = useState<any[]>([]);
    const [viewCount, setViewCount] = useState(0);
    const [isMuted, setIsMuted] = useState(true);
    const [storyReactions, setStoryReactions] = useState<any[]>([]);
    const [myReaction, setMyReaction] = useState<string | null>(null);
    const [hideOverlay, setHideOverlay] = useState(false); // Toggle to hide/show overlay
    
    // Toggle overlay visibility (single tap center while viewing story)
    const toggleOverlay = () => setHideOverlay(!hideOverlay);

    useEffect(() => {
        // Load Sold Stories from LocalStorage
        const savedSold = localStorage.getItem('driver_score_sold_stories');
        if (savedSold) {
            try {
                setSoldStories(JSON.parse(savedSold));
            } catch (e) {
                console.error("Error parsing sold stories from localStorage:", e);
                localStorage.removeItem('driver_score_sold_stories');
            }
        }

        const runInit = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (user?.email) setUserEmail(user.email);
            if (user?.id) setCurrentUserId(user.id);
            
            await fetchStories();
            await cleanupExpiredStories();
        };

        runInit();

        // Timer to refresh visibility every minute
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (selectedStory && currentUserId) {
            // Always fetch views for everyone
            fetchStoryViews(selectedStory.id);
            fetchStoryReactions(selectedStory.id);
            setShowEmbedLink(false);
            setHideOverlay(false); // Reset overlay visibility on new story
            // Record view if not owner
            if (selectedStory.user_id !== currentUserId) {
                recordStoryView(selectedStory.id, currentUserId);
            }
        } else {
            setStoryViews([]);
            setViewCount(0);
            setStoryReactions([]);
            setMyReaction(null);
        }
    }, [selectedStory, currentUserId]);

    // Auto-advance timer for image stories (7 seconds)
    useEffect(() => {
        if (!selectedStory || showEmbedLink) return;
        if (isVideo(selectedStory.media_url)) return; // Videos use onEnded instead

        const timer = setTimeout(() => {
            const idx = visibleStories.findIndex(s => s.id === selectedStory.id);
            if (idx >= 0 && idx < visibleStories.length - 1) {
                setSelectedStory(visibleStories[idx + 1]);
            } else {
                setSelectedStory(null);
            }
        }, 7000);

        return () => clearTimeout(timer);
    }, [selectedStory?.id, showEmbedLink]);

    const recordStoryView = async (storyId: string, viewerId: string) => {
        try {
            await supabase
                .from('story_views')
                .upsert({ story_id: storyId, viewer_id: viewerId }, { onConflict: 'story_id, viewer_id' });
        } catch (err) {
            console.error("Error recording story view:", err);
        }
    };

    const fetchStoryViews = async (storyId: string) => {
        try {
            // Get count
            const { count, error: countError } = await supabase
                .from('story_views')
                .select('*', { count: 'exact', head: true })
                .eq('story_id', storyId);
            
            if (!countError) setViewCount(count || 0);

            // Get viewer details
            const { data, error } = await supabase
                .from('story_views')
                .select(`
                    id,
                    viewer_id,
                    profiles (
                        nickname,
                        avatar_url
                    )
                `)
                .eq('story_id', storyId)
                .order('created_at', { ascending: false });

            if (!error && data) {
                setStoryViews(data);
            }
        } catch (err) {
            console.error("Error fetching story views:", err);
        }
    };

    const fetchStoryReactions = async (storyId: string) => {
        try {
            const { data, error } = await supabase
                .from('story_reactions')
                .select('id, user_id, emoji')
                .eq('story_id', storyId);

            if (!error && data) {
                setStoryReactions(data);
                const mine = data.find((r: any) => r.user_id === currentUserId);
                setMyReaction(mine ? mine.emoji : null);
            }
        } catch (err) {
            console.error("Error fetching reactions:", err);
        }
    };

    const handleReaction = async (storyId: string, emoji: string) => {
        if (!currentUserId) return;
        try {
            if (myReaction === emoji) {
                // Remove reaction (toggle off)
                await supabase
                    .from('story_reactions')
                    .delete()
                    .eq('story_id', storyId)
                    .eq('user_id', currentUserId);
                setMyReaction(null);
            } else {
                // Upsert reaction
                await supabase
                    .from('story_reactions')
                    .upsert(
                        { story_id: storyId, user_id: currentUserId, emoji },
                        { onConflict: 'story_id, user_id' }
                    );
                setMyReaction(emoji);
            }
            // Refresh reactions
            fetchStoryReactions(storyId);
        } catch (err) {
            console.error("Error handling reaction:", err);
        }
    };

    const [videoBlobs, setVideoBlobs] = useState<Set<string>>(new Set());

    const isVideo = (url: string) => {
        if (!url) return false;
        const videoExtensions = ['.mp4', '.mov', '.webm', '.quicktime', '.avi', '.m4v'];
        return videoExtensions.some(ext => url.toLowerCase().includes(ext)) || videoBlobs.has(url);
    };

    // Extract YouTube video ID for embedding
    const getYouTubeEmbedUrl = (url: string): string | null => {
        if (!url) return null;
        const patterns = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/,
            /youtube\.com\/shorts\/([\w-]{11})/,
            /youtube\.com\/embed\/([\w-]{11})/
        ];
        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match) return `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0`;
        }
        return null;
    };

    const isEmbeddable = (url: string): boolean => {
        return !!getYouTubeEmbedUrl(url);
    };

    const cleanupExpiredStories = async () => {
        try {
            // Get current user first if not yet set
            let uid = currentUserId;
            if (!uid) {
                const { data: { user } } = await supabase.auth.getUser();
                uid = user?.id || null;
            }
            if (!uid) return;

            const now = new Date().toISOString();
            console.log(`[Cleanup] Checking for expired stories for user ${uid}...`);

            // 1. Get expired stories for this user
            const { data: expired, error: fetchError } = await supabase
                .from('marketplace_stories')
                .select('id, media_url')
                .eq('user_id', uid)
                .lt('expires_at', now);

            if (fetchError) throw fetchError;
            if (!expired || expired.length === 0) {
                console.log("[Cleanup] No expired stories found.");
                return;
            }

            console.log(`[Cleanup] Found ${expired.length} expired stories. Deleting files then records...`);

            // 2. Delete media from Storage
            const filesToDelete: string[] = [];
            for (const story of expired) {
                if (story.media_url && story.media_url.includes('/avatars/')) {
                    const filePath = story.media_url.split('/avatars/')[1];
                    if (filePath) filesToDelete.push(filePath);
                }
            }

            if (filesToDelete.length > 0) {
                console.log(`[Cleanup] Removing ${filesToDelete.length} files from storage bucket 'avatars'...`);
                const { error: storageError } = await supabase.storage.from('avatars').remove(filesToDelete);
                if (storageError) console.warn("[Cleanup] Storage deletion warning:", storageError);
            }

            // 3. Delete from DB
            const { error: deleteError } = await supabase
                .from('marketplace_stories')
                .delete()
                .in('id', expired.map(s => s.id));

            if (deleteError) throw deleteError;
            
            console.log("[Cleanup] Successfully purged expired stories and media.");
        } catch (err) {
            console.error("[Cleanup] Error:", err);
        }
    };

    const fetchStories = async () => {
        try {
            // Grace window: 2 hours back to avoid newly created stories being filtered by clock drift or timezone issues
            const nowGrace = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
            console.log('[Stories] fetchStories - nowGrace:', nowGrace);

            const { data: storiesData, error: storiesError } = await supabase
                .from('marketplace_stories')
                .select('*')
                .in('status', ['active', 'sold'])
                .gt('expires_at', nowGrace)
                .order('created_at', { ascending: false })
                .limit(50);

            if (storiesError) {
                console.error("Error fetching stories:", storiesError);
                setStories([]);
                return;
            }

            if (!storiesData || storiesData.length === 0) {
                console.warn("No stories found in DB (or none active).");
                setStories([]);
                return;
            }

            // Step 2: Fetch unique profiles for these stories
            const userIds = [...new Set(storiesData.map(s => s.user_id))].filter(Boolean);

            if (userIds.length > 0) {
                const { data: profilesData, error: profilesError } = await supabase
                    .from('profiles')
                    .select('*')
                    .in('id', userIds);

                if (!profilesError && profilesData) {
                    // Step 3: Map profiles back to stories
                    const finalStories = storiesData.map(story => ({
                        ...story,
                        seller: profilesData.find(p => p.id === story.user_id) || null
                    }));
                    setStories(finalStories);
                    console.log("Successfully fetched stories and joined with profiles in JS:", finalStories.length);
                    return;
                }
                console.warn("Could not fetch profiles for stories, showing without seller info", profilesError);
            }

            setStories(storiesData);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const calculatePrice = () => {
        // PROPRIETARY 3-MONTH FREE PROMOTION OVERRIDE
        return "0.00";
    };

    const compressImage = (file: File): Promise<Blob> => {
        return new Promise((resolve) => {
            const reader = new window.FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new window.Image();
                img.src = event.target?.result as string;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const MAX_WIDTH = 800; // Better quality for stories
                    const scaleSize = MAX_WIDTH / img.width;
                    canvas.width = MAX_WIDTH;
                    canvas.height = img.height * scaleSize;

                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                        canvas.toBlob((blob) => {
                            if (blob) resolve(blob);
                        }, 'image/jpeg', 0.8);
                    }
                };
            };
        });
    };

    const [uploadStatus, setUploadStatus] = useState('');

    const compressVideo = (file: File): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            const MAX_SIZE_MB = 48; // Keep under Supabase 50MB limit
            const fileSizeMB = file.size / (1024 * 1024);

            // If already small enough, skip compression
            if (fileSizeMB <= MAX_SIZE_MB) {
                console.log(`Video is ${fileSizeMB.toFixed(1)}MB, no compression needed.`);
                resolve(file);
                return;
            }

            console.log(`Video is ${fileSizeMB.toFixed(1)}MB, compressing...`);
            setUploadStatus(`Comprimindo vídeo (${fileSizeMB.toFixed(0)}MB)...`);

            const video = document.createElement('video');
            video.playsInline = true;
            video.src = URL.createObjectURL(file);

            video.onloadedmetadata = () => {
                // Target resolution: max 720p
                const MAX_WIDTH = 720;
                const MAX_HEIGHT = 1280;
                let width = video.videoWidth;
                let height = video.videoHeight;

                if (width > MAX_WIDTH || height > MAX_HEIGHT) {
                    const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
                    width = Math.round(width * ratio);
                    height = Math.round(height * ratio);
                }
                // Ensure even dimensions (required by many codecs)
                width = width % 2 === 0 ? width : width - 1;
                height = height % 2 === 0 ? height : height - 1;

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d')!;

                const stream = canvas.captureStream(24); // 24 fps
                
                // Try to add audio track if available
                try {
                    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
                    const source = audioCtx.createMediaElementSource(video);
                    const dest = audioCtx.createMediaStreamDestination();
                    source.connect(dest);
                    // We DO NOT connect to audioCtx.destination to prevent the audio from playing out loud during compression
                    dest.stream.getAudioTracks().forEach(t => stream.addTrack(t));
                } catch (e) {
                    console.warn("Could not capture audio track:", e);
                }

                // Target bitrate: ~1.5 Mbps for good quality at 720p
                const recorder = new MediaRecorder(stream, {
                    mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9') 
                        ? 'video/webm;codecs=vp9' 
                        : 'video/webm',
                    videoBitsPerSecond: 1_500_000
                });

                const chunks: Blob[] = [];
                recorder.ondataavailable = (e) => {
                    if (e.data.size > 0) chunks.push(e.data);
                };

                recorder.onstop = () => {
                    const blob = new Blob(chunks, { type: 'video/webm' });
                    const newSizeMB = blob.size / (1024 * 1024);
                    console.log(`Compressed: ${fileSizeMB.toFixed(1)}MB → ${newSizeMB.toFixed(1)}MB`);
                    setUploadStatus(`Comprimido: ${newSizeMB.toFixed(1)}MB. Enviando...`);
                    URL.revokeObjectURL(video.src);
                    resolve(blob);
                };

                recorder.onerror = (e) => {
                    console.error("MediaRecorder error:", e);
                    URL.revokeObjectURL(video.src);
                    // Fallback: return original file
                    reject(new Error('COMPRESSION_FAILED'));
                };

                video.onplay = () => {
                    recorder.start(1000); // Collect data every second

                    const drawFrame = () => {
                        if (video.ended || video.paused) {
                            recorder.stop();
                            return;
                        }
                        ctx.drawImage(video, 0, 0, width, height);
                        requestAnimationFrame(drawFrame);
                    };
                    drawFrame();
                };

                video.onended = () => {
                    if (recorder.state === 'recording') recorder.stop();
                };

                video.play().catch(err => {
                    console.error("Video play failed:", err);
                    URL.revokeObjectURL(video.src);
                    reject(new Error('COMPRESSION_FAILED'));
                });
            };

            video.onerror = () => {
                URL.revokeObjectURL(video.src);
                reject(new Error('COMPRESSION_FAILED'));
            };
        });
    };

    const handleCreateAd = async () => {
        if (!selectedFile && !newAd.media_url) {
            alert("Por favor, selecione uma imagem.");
            return;
        }

        setIsUploading(true);
        setUploadStatus('Preparando...');
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error("Usuário não autenticado");

            // Check if user is Premium
            const { data: profile } = await supabase
                .from('profiles')
                .select('is_premium')
                .eq('id', user.id)
                .single();

            const isPremium = profile?.is_premium || isPremiumOrVip;
            const durationHours = Number(newAd.duration) || 24;
            // Always use a fresh Date.now() right before creating to avoid any stale reference
            const nowMs = Date.now();
            const durationMs = durationHours * 60 * 60 * 1000;
            const expiresAt = new Date(nowMs + durationMs);

            console.log('[Stories] Creating ad -', {
                durationHours,
                durationMs,
                nowMs,
                nowISO: new Date(nowMs).toISOString(),
                expiresAtISO: expiresAt.toISOString(),
                expiresAtLocal: expiresAt.toLocaleString()
            });

            let finalMediaUrl = newAd.media_url;

            // 1. Upload to Supabase Storage if it's a new file
            if (selectedFile) {
                const isVideoFile = selectedFile.type.startsWith('video/');
                const fileSizeMB = selectedFile.size / (1024 * 1024);
                console.log(`Starting ${isVideoFile ? 'video' : 'image'} upload... ${fileSizeMB.toFixed(1)}MB`, selectedFile.name);
                
                let uploadData: Blob | File = selectedFile;
                let extension = selectedFile.name.split('.').pop() || (isVideoFile ? 'mp4' : 'jpg');
                let contentType = selectedFile.type;

                if (!isVideoFile) {
                    setUploadStatus('Comprimindo imagem...');
                    uploadData = await compressImage(selectedFile);
                    extension = 'jpg';
                    contentType = 'image/jpeg';
                } else {
                    // Compress video if too large
                    try {
                        setUploadStatus(`Processando vídeo (${fileSizeMB.toFixed(0)}MB)...`);
                        uploadData = await compressVideo(selectedFile);
                        if (uploadData !== selectedFile) {
                            extension = 'webm';
                            contentType = 'video/webm';
                        }
                    } catch (compErr: any) {
                        if (compErr.message === 'COMPRESSION_FAILED' && fileSizeMB > 48) {
                            alert(`Vídeo muito grande (${fileSizeMB.toFixed(0)}MB). O limite é 48MB e a compressão falhou. Tente gravar um vídeo mais curto ou em menor resolução.`);
                            setIsUploading(false);
                            setUploadStatus('');
                            return;
                        }
                        // If compression failed but file is small enough, try raw upload
                        console.warn("Compression failed, trying raw upload...");
                    }
                }

                const finalSizeMB = (uploadData as Blob).size / (1024 * 1024);
                if (finalSizeMB > 48) {
                    alert(`Arquivo ainda é muito grande (${finalSizeMB.toFixed(0)}MB). O limite é 48MB. Tente um vídeo mais curto.`);
                    setIsUploading(false);
                    setUploadStatus('');
                    return;
                }
                
                // Use the same path pattern as ProfileView.tsx to respect bucket policies
                const fileName = `${user.id}/story_${Date.now()}.${extension}`;

                setUploadStatus(`Enviando (${finalSizeMB.toFixed(1)}MB)...`);
                console.log("Uploading to bucket 'avatars' with path:", fileName, `(${finalSizeMB.toFixed(1)}MB)`);
                const { error: uploadError } = await supabase.storage
                    .from('avatars')
                    .upload(fileName, uploadData, {
                        contentType,
                        cacheControl: '3600'
                    });

                if (uploadError) {
                    console.error("Storage upload error:", uploadError);
                    if (uploadError.message?.includes('size') || uploadError.message?.includes('413')) {
                        throw new Error(`Arquivo muito grande para upload. Tente um vídeo mais curto ou em menor qualidade.`);
                    }
                    throw uploadError;
                }

                const { data: { publicUrl } } = supabase.storage
                    .from('avatars')
                    .getPublicUrl(fileName);

                console.log("Public URL generated:", publicUrl);
                finalMediaUrl = publicUrl;
            }

            setUploadStatus('Publicando anúncio...');

            const payload: any = {
                title: newAd.title,
                media_url: finalMediaUrl,
                link_url: newAd.link_url || null,
                expires_at: expiresAt.toISOString(),
                status: 'active',
                user_id: user?.id
            };

            console.log("Ad payload prepared:", payload);

            let { error } = await supabase.from('marketplace_stories').insert(payload);

            if (error) {
                console.error("Supabase insert error:", error);
                throw error;
            }

            // Award XP for ad creation
            if (user?.id) {
                await addXpToProfile(user.id, XP_VALUES.AD_CREATED, 'STORY');
            }

            console.log("Successfully published story to DB.");

            alert("Anúncio criado com sucesso!");
            setShowAdModal(false);
            setStep(1);
            setNewAd({ title: '', media_url: '', duration: 24, link_url: '' });
            setSelectedFile(null);
            fetchStories();

        } catch (err: any) {
            console.error("Error creating ad:", err);
            alert(err?.message || "Erro ao criar anúncio. Tente novamente.");
        } finally {
            setIsUploading(false);
            setUploadStatus('');
        }
    };

    const handleMarkAsSold = async (story: Story) => {
        if (!confirm("Confirmar venda? O anúncio ficará visível por mais tempo como \"Vendido\".")) return;

        const now = new Date();
        const created = story.created_at ? new Date(story.created_at) : new Date();
        const activeTimeMs = now.getTime() - created.getTime();
        const originalExpiry = new Date(story.expires_at);

        // Extension: same as active time, but minimum 3h and capped at 24h
        const extensionMs = Math.max(3 * 60 * 60 * 1000, Math.min(activeTimeMs, 24 * 60 * 60 * 1000));
        const bonusExpiry = new Date(now.getTime() + extensionMs);

        // Use the longer duration: either the remaining purchased time or the sold extension bonus
        const finalExpiry = bonusExpiry > originalExpiry ? bonusExpiry : originalExpiry;
        const finalExpiryISO = finalExpiry.toISOString();

        const soldState: SoldState = {
            soldAt: now.toISOString(),
            originalExpiresAt: story.expires_at,
            extendedExpiresAt: finalExpiryISO
        };

        // Update local state first for immediate UI response
        setSoldStories(prev => {
            const next = { ...prev, [story.id]: soldState };
            localStorage.setItem('driver_score_sold_stories', JSON.stringify(next));
            return next;
        });

        // Update selectedStory if it exists to refresh the modal immediately
        if (selectedStory && selectedStory.id === story.id) {
            setSelectedStory({ ...selectedStory, status: 'sold', expires_at: finalExpiryISO });
        }
        
        // Also update stories list so filtering relies on the updated data internally
        setStories(prev => prev.map(s => s.id === story.id ? { ...s, status: 'sold', expires_at: finalExpiryISO } : s));

        // Sync with Database: update status AND expires_at so it stays visible in fetch query
        try {
            const { error } = await supabase
                .from('marketplace_stories')
                .update({
                    status: 'sold',
                    expires_at: finalExpiryISO
                })
                .eq('id', story.id);

            if (error) throw error;
            console.log("Successfully updated story to sold in DB with final expiry:", finalExpiryISO);

            // Award XP for selling
            if (story.user_id) {
                await addXpToProfile(story.user_id, XP_VALUES.AD_SOLD, 'STORY');
            }
        } catch (dbErr) {
            console.error("Error updating status to sold:", dbErr);
        }

        alert(`Vendido! O anúncio ficará no ar como "Vendido" até ${finalExpiry.toLocaleString()}`);
        if (story.id !== 'demo-1') {
            fetchStories();
        }
    };

    const handleDeleteStory = async (storyId: string, mediaUrl: string) => {
        if (!confirm("Tem certeza que deseja apagar este anúncio permanentemente?")) return;

        try {
            // First: Delete dependent views to prevent foreign key errors
            await supabase.from('story_views').delete().eq('story_id', storyId);

            // 1. Delete media if it's stored in our bucket
            if (mediaUrl && mediaUrl.includes('/avatars/')) {
                const filePath = mediaUrl.split('/avatars/')[1];
                if (filePath) {
                    await supabase.storage.from('avatars').remove([filePath]);
                }
            }

            // 2. Delete from database
            const { error, count } = await supabase
                .from('marketplace_stories')
                .delete({ count: 'exact' })
                .eq('id', storyId);

            if (error) throw error;
            if (count === 0) {
                // If DELETE is blocked by RLS policies (common for 'sold' ads), do a soft-delete via UPDATE
                const { error: updateError } = await supabase
                    .from('marketplace_stories')
                    .update({ 
                        status: 'deleted', 
                        expires_at: new Date(0).toISOString() // Expire instantly
                    })
                    .eq('id', storyId);
                    
                if (updateError) {
                    throw new Error("Não foi possível excluir o anúncio. Provavelmente você não tem permissão ou o anúncio já foi excluído.");
                }
                console.log("Hard delete blocked by RLS, performed soft-delete via UPDATE instead.");
            } else {
                console.log("Successfully deleted story", storyId);
            }
            
            // 3. Update UI and Local Cache ONLY after successful DB deletion
            setSoldStories(prev => {
                const next = { ...prev };
                delete next[storyId];
                localStorage.setItem('driver_score_sold_stories', JSON.stringify(next));
                return next;
            });
            
            setStories(prev => prev.filter(s => s.id !== storyId));
            setSelectedStory(null);
            alert("Anúncio removido com sucesso!");

        } catch (err) {
            console.error("Error deleting story:", err);
            alert("Erro ao remover o anúncio.");
        }
    };

    // Helper to check if current user owns the story
    // For Preview: If no user_id in story, assume it's NOT ours unless we just created it (but we fetch from DB).
    // Logic: We need user_id from DB. If missing, we can't really verify. 
    // UPDATE: We added user_id to types.ts, assuming view returns it.
    // Hack for preview: AccessControl allows specific email. For this task, we assume the user logged in is the owner if user_id matches OR if we are in a 'demo' mood.
    // Actual logic: match user id.
    const isOwner = (story: Story) => {
        if (!currentUserId) return false;
        return story.user_id === currentUserId;
    };


    if (loading) return <div className="h-24 animate-pulse bg-slate-800 rounded-xl mb-6"></div>;

    const visibleStories = stories.filter(story => {
        if (!story || !story.expires_at) return false;

        // Use extended expiration if marked as sold locally
        const soldData = soldStories[story.id];
        const expiry = soldData ? soldData.extendedExpiresAt : story.expires_at;

        const expiryDate = new Date(expiry);
        const isVisible = expiryDate > currentTime;

        if (!isVisible) console.log(`Story ${story.id} filtered out. Expiry: ${expiry}, Now: ${currentTime.toISOString()}`);
        return isVisible;
    });

    const getRemainingTime = (expiryStr?: string) => {
        if (!expiryStr) return '--';
        const expiry = new Date(expiryStr);
        if (isNaN(expiry.getTime())) return '--';

        const diff = expiry.getTime() - currentTime.getTime();
        if (diff <= 0) return 'Expirado';

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 0) return `${hours}h ${mins}m`;
        return `${mins}m`;
    };

    const formatPostingTime = (createdAt?: string) => {
        if (!createdAt) return '--:--';
        const date = new Date(createdAt);
        if (isNaN(date.getTime())) return '--:--';
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const goToNextStory = () => {
        const idx = visibleStories.findIndex(s => s.id === selectedStory?.id);
        if (idx >= 0 && idx < visibleStories.length - 1) {
            setSelectedStory(visibleStories[idx + 1]);
        } else {
            setSelectedStory(null); // Close if last story
        }
    };

    const goToPrevStory = () => {
        const idx = visibleStories.findIndex(s => s.id === selectedStory?.id);
        if (idx > 0) {
            setSelectedStory(visibleStories[idx - 1]);
        }
    };

    console.log("Visible stories after filter:", visibleStories.length);

    return (
        <div id="tour-marketplace" className="mb-8">
            <div className="mb-6">
                <PartnerCarousel onAnunciarClick={() => setShowAdModal(true)} />
            </div>

            <div className="flex justify-between items-center px-1 mb-3">
                <h3 className="text-sm font-bold text-slate-400">Novidades & Parceiros</h3>
                <button
                    onClick={() => {
                        setShowAdModal(true);
                    }}
                    className="text-xs font-bold flex items-center gap-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-3 py-1.5 rounded-full shadow-lg shadow-orange-500/30 transition-all hover:scale-105 active:scale-95 border border-orange-400/50"
                >
                    <Plus className="w-4 h-4" /> Anunciar
                </button>
            </div>

            <div className="flex gap-4 overflow-x-auto pb-4 px-1 snap-x no-scrollbar">
                {visibleStories.map((story) => {
                    const isSold = !!soldStories[story.id] || story.status === 'sold';
                    return (
                        <button
                            key={story.id}
                            onClick={() => setSelectedStory(story)}
                            className="flex-shrink-0 flex flex-col items-center gap-2 group snap-start relative"
                        >
                            <div className={`w-20 aspect-[9/16] rounded-xl p-[2px] ${isSold ? 'bg-slate-600' : 'bg-gradient-to-tr from-orange-500 to-purple-600'} group-hover:scale-105 transition-transform overflow-hidden`}>
                                <div className="w-full h-full rounded-lg border-2 border-slate-900 overflow-hidden relative bg-slate-800">
                                    {isVideo(story.media_url) ? (
                                        <video 
                                            src={story.media_url} 
                                            className={`w-full h-full object-cover ${isSold ? 'grayscale opacity-50' : ''}`}
                                            muted
                                            playsInline
                                            autoPlay
                                            loop
                                        />
                                    ) : (
                                        <img src={story.media_url} alt={story.title} className={`w-full h-full object-cover ${isSold ? 'grayscale opacity-50' : ''}`} />
                                    )}
                                    <div className="absolute top-1 right-1 bg-black/60 backdrop-blur-sm px-1 rounded text-[8px] text-white font-medium">
                                        {getRemainingTime(soldStories[story.id]?.extendedExpiresAt || story.expires_at)}
                                    </div>
                                    {isSold && (
                                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                            <span className="text-[8px] font-bold text-white bg-red-600 px-1 py-0.5 rounded shadow-lg transform -rotate-12 border border-white/20">VENDIDO</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <span className="text-[10px] text-slate-300 max-w-[64px] truncate text-center font-medium">
                                {story.title || 'Oferta'}
                            </span>
                        </button>
                    );
                })}
                {visibleStories.length === 0 && (
                    <div className="text-xs text-slate-500 italic p-2">Nenhum story ativo no momento.</div>
                )}
            </div>

            {/* Create Ad Modal */}
            {showAdModal && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowAdModal(false)}>
                    <div className="bg-slate-900 w-full max-w-md rounded-2xl border border-slate-700 p-6" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-bold text-white text-lg">Criar Anúncio</h3>
                            <button onClick={() => setShowAdModal(false)}><X className="text-slate-400" /></button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label htmlFor="ad_title" className="text-xs text-slate-400 mb-1 block">Título</label>
                                <input
                                    id="ad_title"
                                    name="title"
                                    className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                                    placeholder="Ex: Vendo Corolla 2020"
                                    value={newAd.title}
                                    onChange={e => setNewAd({ ...newAd, title: e.target.value })}
                                />
                            </div>

                            <div>
                                <label htmlFor="ad_media" className="text-xs text-slate-400 mb-1 block">Imagem do Story</label>

                                {newAd.media_url ? (
                                    <div className="relative aspect-[9/16] w-32 bg-slate-800 rounded-lg overflow-hidden border border-slate-700 mx-auto">
                                        {isVideo(newAd.media_url) ? (
                                            <video 
                                                src={newAd.media_url} 
                                                className="w-full h-full object-cover"
                                                autoPlay 
                                                loop 
                                                muted 
                                                playsInline
                                            />
                                        ) : (
                                            <img src={newAd.media_url} alt="Preview" className="w-full h-full object-cover" />
                                        )}
                                        <button
                                            onClick={() => setNewAd({ ...newAd, media_url: '' })}
                                            className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 hover:bg-red-500 transition-colors"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="border-2 border-dashed border-slate-700 rounded-xl p-6 flex flex-col items-center justify-center gap-2 hover:border-orange-500/50 hover:bg-slate-800/50 transition-all group cursor-pointer relative">
                                        <input
                                            id="ad_media"
                                            name="media_url"
                                            type="file"
                                            accept="image/*,video/*"
                                            className="absolute inset-0 opacity-0 cursor-pointer z-10"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    const url = URL.createObjectURL(file);
                                                    if (file.type.startsWith('video/')) {
                                                        setVideoBlobs(prev => new Set(prev).add(url));
                                                    }
                                                    setNewAd({ ...newAd, media_url: url });
                                                    setSelectedFile(file);
                                                }
                                            }}
                                        />
                                        <div className="p-3 bg-slate-800 rounded-full group-hover:scale-110 transition-transform">
                                            <Upload className="w-6 h-6 text-orange-500" />
                                        </div>
                                        <p className="text-xs text-slate-400 font-medium">Toque para enviar foto ou vídeo</p>
                                        <p className="text-[10px] text-slate-600">Recomendado: 9:16 (Vertical)</p>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label htmlFor="ad_link" className="text-xs text-slate-400 mb-1 block">Link (opcional) — Instagram, YouTube, TikTok...</label>
                                <input
                                    id="ad_link"
                                    name="link_url"
                                    className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white text-sm focus:ring-2 focus:ring-orange-500 outline-none mb-4 placeholder-slate-500"
                                    placeholder="https://youtube.com/shorts/... ou instagram.com/reel/..."
                                    value={newAd.link_url}
                                    onChange={e => setNewAd({ ...newAd, link_url: e.target.value })}
                                />
                            </div>

                            <div>
                                <label htmlFor="ad_duration" className="text-xs text-slate-400 mb-1 block">Duração (Horas)</label>
                                <select
                                    id="ad_duration"
                                    name="duration"
                                    className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-white text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                                    value={newAd.duration}
                                    onChange={e => {
                                        const val = Number(e.target.value);
                                        setNewAd({ ...newAd, duration: val });
                                    }}
                                >
                                    <option value={12}>12 Horas</option>
                                    <option value={24}>24 Horas</option>
                                    <option value={48}>48 Horas</option>
                                    <option value={168}>1 Semana</option>
                                </select>
                            </div>

                            <div className="pt-4 border-t border-slate-800 flex justify-center items-center">
                                <button
                                    disabled={!newAd.title || !newAd.media_url || isUploading}
                                    onClick={handleCreateAd}
                                    className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
                                >
                                    {isUploading && <Loader2 className="w-4 h-4 animate-spin" />}
                                    {isUploading ? (uploadStatus || 'Enviando...') : 'Publicar Anúncio 🚀'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Story Viewer Modal */}
            {selectedStory && (
                <div className="fixed inset-0 z-[100] bg-black backdrop-blur-sm flex items-center justify-center animate-fade-in" onClick={() => setSelectedStory(null)}>
                    <div className="relative w-full h-full md:max-w-md md:h-[90vh] md:rounded-2xl overflow-hidden shadow-2xl bg-black md:border border-slate-800" onClick={e => e.stopPropagation()}>

                        {/* Progress Bars - one per story */}
                        <div className="absolute top-2 left-2 right-2 flex gap-1 z-20">
                            {visibleStories.map((s, i) => {
                                const currentIdx = visibleStories.findIndex(vs => vs.id === selectedStory.id);
                                return (
                                    <div key={s.id} className="h-1 bg-white/30 flex-1 rounded-full overflow-hidden">
                                        {i < currentIdx ? (
                                            <div className="h-full bg-white w-full"></div>
                                        ) : i === currentIdx ? (
                                            <div className={`h-full bg-white w-full ${isVideo(selectedStory.media_url) ? '' : 'animate-progress'}`}></div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>

                        <button
                            onClick={() => setSelectedStory(null)}
                            className="absolute top-4 right-4 z-20 text-white drop-shadow-md hover:bg-black/20 p-1 rounded-full bg-black/20"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsMuted(!isMuted);
                            }}
                            className="absolute top-4 right-14 z-20 text-white drop-shadow-md hover:bg-black/20 p-1 rounded-full bg-black/20 transition-all active:scale-95"
                            title={isMuted ? "Ativar som" : "Desativar som"}
                        >
                            {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                        </button>

                        {/* Posting & Expiry Info - with toggle visibility */}
                        <div className={`absolute top-4 left-4 z-20 flex flex-col gap-0.5 transition-opacity duration-300 ${hideOverlay ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                            <span className="text-[10px] text-white/80 font-medium drop-shadow-md bg-black/20 px-2 py-0.5 rounded-full backdrop-blur-sm">
                                Postado às {formatPostingTime(selectedStory.created_at)}
                            </span>
                            <span className="text-[10px] text-orange-400 font-bold drop-shadow-md bg-black/20 px-2 py-0.5 rounded-full backdrop-blur-sm">
                                Expira em {getRemainingTime(soldStories[selectedStory.id]?.extendedExpiresAt || selectedStory.expires_at)}
                            </span>
                            <span className="text-[10px] text-cyan-400 font-bold drop-shadow-md bg-black/40 px-2 py-0.5 rounded-full backdrop-blur-sm flex items-center gap-1 w-fit">
                                <Eye className="w-3 h-3" /> {viewCount} visualizações
                            </span>
                        </div>

                        <div className="relative w-full h-full bg-black flex items-center justify-center">
                            {/* Tap zones for navigation + overlay toggle */}
                            <div className="absolute inset-0 z-10 flex">
                                <div className="w-1/3 h-full cursor-pointer" onClick={(e) => { e.stopPropagation(); goToPrevStory(); }}></div>
                                <div className="w-1/3 h-full cursor-pointer" onClick={(e) => { 
                                    e.stopPropagation(); 
                                    toggleOverlay();
                                    // Enter fullscreen for both video and image
                                    if (!document.fullscreenElement && e.currentTarget.parentElement?.parentElement) {
                                        e.currentTarget.parentElement.parentElement.requestFullscreen();
                                    }
                                }} title="Toque para mostrar/ocultar textos"></div>
                                <div className="w-1/3 h-full cursor-pointer" onClick={(e) => { e.stopPropagation(); goToNextStory(); }}></div>
                            </div>

                            {isVideo(selectedStory.media_url) ? (
                                <video 
                                    key={selectedStory.id}
                                    src={selectedStory.media_url} 
                                    className={`max-w-full max-h-full object-contain ${(soldStories[selectedStory.id] || selectedStory.status === 'sold') ? 'grayscale opacity-50' : ''}`}
                                    autoPlay 
                                    muted={false} 
                                    playsInline
                                    onEnded={goToNextStory}
                                />
                            ) : (
                                <img
                                    src={selectedStory.media_url}
                                    alt={selectedStory.title}
                                    className={`max-w-full max-h-full object-contain ${(soldStories[selectedStory.id] || selectedStory.status === 'sold') ? 'grayscale opacity-50' : ''}`}
                                />
                            )}

                            {(soldStories[selectedStory.id] || selectedStory.status === 'sold') && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                                    <div className="bg-red-600 text-white font-black text-5xl px-8 py-3 transform -rotate-12 border-4 border-white shadow-2xl transition-all animate-in zoom-in duration-300">
                                        VENDIDO
                                    </div>
                                </div>
                            )}

                            {/* Story Details & Viewer List (Bottom Section) - with toggle visibility */}
                            <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/80 to-transparent p-6 pt-24 z-20 transition-opacity duration-300 ${hideOverlay ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                                
                                {/* Emoji Reactions Bar */}
                                <div className="mb-3 flex items-center gap-1">
                                    {['👍', '❤️', '😂', '🔥', '👏', '😍'].map(emoji => (
                                        <button
                                            key={emoji}
                                            onClick={(e) => { e.stopPropagation(); handleReaction(selectedStory.id, emoji); }}
                                            className={`text-xl p-1.5 rounded-full transition-all active:scale-125 ${
                                                myReaction === emoji 
                                                ? 'bg-white/20 scale-110 ring-2 ring-orange-400' 
                                                : 'bg-white/5 hover:bg-white/15'
                                            }`}
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                    {storyReactions.length > 0 && (
                                        <span className="text-[10px] text-slate-400 font-bold ml-2">{storyReactions.length}</span>
                                    )}
                                </div>

                                {/* Viewer List - Visible to everyone */}
                                {storyViews.length > 0 && (
                                    <div className="mb-4 bg-white/5 p-3 rounded-xl backdrop-blur-md border border-white/10">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <Users className="w-3.5 h-3.5 text-cyan-400" />
                                                <span className="text-[10px] font-black text-white uppercase tracking-widest">Visto por</span>
                                            </div>
                                            <span className="text-[10px] text-slate-400 font-bold">{viewCount} total</span>
                                        </div>
                                        <div className="flex -space-x-1.5 overflow-x-auto pb-1 scrollbar-hide">
                                            {storyViews.map((view) => (
                                                <button 
                                                    key={view.id} 
                                                    className="relative group shrink-0 cursor-pointer hover:z-10 hover:scale-110 transition-transform"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (view.viewer_id) {
                                                            onViewProfile(view.viewer_id);
                                                            setSelectedStory(null);
                                                        }
                                                    }}
                                                >
                                                    {view.profiles?.avatar_url ? (
                                                        <img
                                                            src={view.profiles.avatar_url}
                                                            alt={view.profiles.nickname}
                                                            className="w-7 h-7 rounded-full border-2 border-slate-900 object-cover"
                                                            title={view.profiles.nickname}
                                                        />
                                                    ) : (
                                                        <div className="w-7 h-7 rounded-full border-2 border-slate-900 bg-slate-800 flex items-center justify-center" title={view.profiles?.nickname || 'Motorista'}>
                                                            <User className="w-3.5 h-3.5 text-slate-500" />
                                                        </div>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                        <p className="text-[9px] text-slate-400 mt-2 font-medium">
                                            {storyViews.length === 1
                                                ? `${storyViews[0].profiles?.nickname || 'Alguém'} visualizou.`
                                                : `${storyViews[0].profiles?.nickname || 'Alguém'} e outros ${storyViews.length - 1} viram.`
                                            }
                                        </p>
                                    </div>
                                )}

                                <h3 className="text-xl font-black text-white mb-2 drop-shadow-lg">{selectedStory.title}</h3>

                                {/* Social Link */}
                                {selectedStory.link_url && (
                                    <div className="mb-3">
                                        {showEmbedLink && isEmbeddable(selectedStory.link_url) ? (
                                            <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-white/10 mb-2">
                                                <iframe
                                                    src={getYouTubeEmbedUrl(selectedStory.link_url)!}
                                                    className="w-full h-full"
                                                    allow="autoplay; encrypted-media"
                                                    allowFullScreen
                                                    title="Video"
                                                />
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setShowEmbedLink(false); }}
                                                    className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded-full z-10"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (isEmbeddable(selectedStory.link_url!)) {
                                                        setShowEmbedLink(true);
                                                    } else {
                                                        window.open(selectedStory.link_url!, '_blank');
                                                    }
                                                }}
                                                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold py-2.5 px-4 rounded-xl transition-all active:scale-95 text-sm shadow-lg"
                                            >
                                                <ExternalLink className="w-4 h-4" />
                                                {isEmbeddable(selectedStory.link_url!) ? '▶ Assistir Vídeo' : '🔗 Abrir Link'}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {(soldStories[selectedStory.id] || selectedStory.status === 'sold') ? (
                                    <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-600 text-center">
                                        <div className="mb-4">
                                            <p className="text-slate-300 text-sm font-medium mb-1">Este item já foi vendido!</p>
                                            <p className="text-xs text-slate-500">O anúncio continua visível por tempo limitado para seu networking.</p>
                                        </div>
                                        
                                        {/* Delete Button for Sold Stories */}
                                        {isOwner(selectedStory) && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteStory(selectedStory.id, selectedStory.media_url);
                                                }}
                                                className="w-full bg-red-900/30 hover:bg-red-800/40 active:scale-95 text-red-400 font-bold py-2 px-4 rounded-lg text-center transition-all flex items-center justify-center gap-2 border border-red-900/50 text-sm"
                                            >
                                                <X className="w-4 h-4" /> Remover Anúncio Vencido
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {selectedStory.user_id && (
                                            <div className="space-y-2 mb-4">
                                                {/* WhatsApp Contact - Open to all as requested */}
                                                {selectedStory.seller?.phone ? (
                                                    <a
                                                        href={`https://wa.me/55${selectedStory.seller.phone.replace(/\D/g, '')}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-4 rounded-xl transition-all"
                                                    >
                                                        <span>📱</span> Contato WhatsApp
                                                    </a>
                                                ) : (
                                                    <p className="text-xs text-slate-500 text-center py-2">Vendedor não informou WhatsApp</p>
                                                )}

                                                <button
                                                    onClick={() => {
                                                        onViewProfile(selectedStory.user_id!);
                                                        setSelectedStory(null);
                                                    }}
                                                    className="block w-full text-white font-bold py-3 px-4 rounded-xl text-center transition-all flex items-center justify-center gap-2"
                                                    style={{
                                                        background: 'rgba(255, 255, 255, 0.15)',
                                                        backdropFilter: 'blur(12px)',
                                                        WebkitBackdropFilter: 'blur(12px)',
                                                        border: '1px solid rgba(255, 255, 255, 0.3)',
                                                    }}
                                                >
                                                    <User className="w-5 h-5" /> Ver Perfil do Vendedor
                                                </button>
                                            </div>
                                        )}

                                        {/* Mark as Sold Button */}
                                        {isOwner(selectedStory) && (
                                            <div className="flex gap-2 w-full mt-4">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleMarkAsSold(selectedStory);
                                                    }}
                                                    className="flex-1 bg-slate-800 hover:bg-slate-700 active:scale-95 text-slate-300 font-bold py-3 px-2 rounded-xl text-center transition-all flex items-center justify-center gap-1.5 border border-slate-700 shadow-lg text-sm"
                                                >
                                                    <DollarSign className="w-4 h-4 text-green-500" /> Vender
                                                </button>
                                                
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteStory(selectedStory.id, selectedStory.media_url);
                                                    }}
                                                    className="flex-1 bg-red-900/30 hover:bg-red-800/40 active:scale-95 text-red-400 font-bold py-3 px-2 rounded-xl text-center transition-all flex items-center justify-center gap-1.5 border border-red-900/50 shadow-lg text-sm"
                                                >
                                                    <X className="w-4 h-4" /> Apagar
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
